import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Container, Form, Row, Col, Table, Alert } from "react-bootstrap";

import ModalAjusteCtaCte from "../components/ModalAjusteCtaCte";
import {
  getAjustesCtaCte,
  anularAjusteCtaCte,
} from "../helpers/rutaAjustesCtaCte";

const hoyISO = () => new Date().toISOString().slice(0, 10);

const fmtFecha = (f) => {
  if (!f) return "-";
  const s = String(f).slice(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const nombreEntidad = (a) => {
  if (a?.entidadTipo === "CLIENTE") {
    return a?.cliente?.razonsocial || a?.cliente?.cliente || "-";
  }

  return a?.proveedor?.razonsocial || a?.proveedor?.proveedor || "-";
};

const estadoColor = (estado) => {
  if (estado === "PENDIENTE") return "#0d6efd";
  if (estado === "PARCIAL") return "#fd7e14";
  if (estado === "APLICADO") return "#198754";
  if (estado === "ANULADO") return "#dc3545";
  return "#6c757d";
};

export default function InformeAjustesCtaCte() {
  const [ajustes, setAjustes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [ajusteEdit, setAjusteEdit] = useState(null);

  const [filtros, setFiltros] = useState({
    desde: "2026-01-01",
    hasta: hoyISO(),
    entidadTipo: "",
    movimiento: "",
    estado: "",
    buscar: "",
  });

  const setF = (k, v) =>
    setFiltros((prev) => ({
      ...prev,
      [k]: v,
    }));

  const consultar = async () => {
    setLoading(true);
    setErr("");

    try {
      const params = {
        desde: filtros.desde,
        hasta: filtros.hasta,
        entidadTipo: filtros.entidadTipo,
        movimiento: filtros.movimiento,
        estado: filtros.estado,
      };

      const data = await getAjustesCtaCte(params);
      setAjustes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("❌ getAjustesCtaCte:", e);
      setErr(e?.message || "No se pudieron cargar los ajustes.");
      setAjustes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    consultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ajustesFiltrados = useMemo(() => {
    const b = String(filtros.buscar || "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toUpperCase()
      .trim();

    if (!b) return ajustes;

    return ajustes.filter((a) => {
      const texto = [
        a.numero,
        a.entidadTipo,
        nombreEntidad(a),
        a.movimiento,
        a.concepto,
        a.estado,
      ]
        .join(" ")
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toUpperCase();

      return texto.includes(b);
    });
  }, [ajustes, filtros.buscar]);

  const abrirNuevo = () => {
    setAjusteEdit(null);
    setShowModal(true);
  };

  const abrirEditar = (ajuste) => {
    if (ajuste.estado === "ANULADO") {
      alert("No se puede editar un ajuste anulado.");
      return;
    }

    if (Number(ajuste.aplicadoTotal || 0) > 0) {
      alert("No se puede editar un ajuste que ya fue aplicado.");
      return;
    }

    setAjusteEdit(ajuste);
    setShowModal(true);
  };

  const anular = async (ajuste) => {
    if (ajuste.estado === "ANULADO") return;

    if (Number(ajuste.aplicadoTotal || 0) > 0) {
      alert("No se puede anular un ajuste que ya fue aplicado.");
      return;
    }

    const ok = window.confirm(
      `¿Anular el ajuste ${ajuste.numero}?\n\nEsta acción no borra el registro, solo lo deja anulado.`
    );

    if (!ok) return;

    try {
      const resp = await anularAjusteCtaCte(ajuste._id);

      if (!resp?.ok) {
        throw new Error(resp?.error || resp?.msg || "No se pudo anular.");
      }

      consultar();
    } catch (e) {
      console.error("❌ anular ajuste:", e);
      alert(e?.message || "Error anulando ajuste.");
    }
  };

  return (
    <Container fluid="md" className="py-3" translate="no">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Ajustes de Cuenta Corriente</h4>

            <Button
              variant="warning"
              onClick={abrirNuevo}
              style={{
                fontWeight: 700,
                color: "#111",
                border: "1px solid #b36b00",
                boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
              }}
            >
              + Nuevo ajuste
            </Button>
          </div>

          <Row className="g-2 align-items-end">
            <Col md={2}>
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                value={filtros.desde}
                onChange={(e) => setF("desde", e.target.value)}
              />
            </Col>

            <Col md={2}>
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                value={filtros.hasta}
                onChange={(e) => setF("hasta", e.target.value)}
              />
            </Col>

            <Col md={2}>
              <Form.Label>Entidad</Form.Label>
              <Form.Select
                value={filtros.entidadTipo}
                onChange={(e) => setF("entidadTipo", e.target.value)}
              >
                <option value="">Todos</option>
                <option value="CLIENTE">Cliente</option>
                <option value="PROVEEDOR">Proveedor</option>
              </Form.Select>
            </Col>

            <Col md={2}>
              <Form.Label>Movimiento</Form.Label>
              <Form.Select
                value={filtros.movimiento}
                onChange={(e) => setF("movimiento", e.target.value)}
              >
                <option value="">Todos</option>
                <option value="A_COBRAR">A cobrar</option>
                <option value="A_PAGAR">A pagar</option>
              </Form.Select>
            </Col>

            <Col md={2}>
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={filtros.estado}
                onChange={(e) => setF("estado", e.target.value)}
              >
                <option value="">Todos</option>
                <option value="PENDIENTE">Pendiente</option>
                <option value="PARCIAL">Parcial</option>
                <option value="APLICADO">Aplicado</option>
                <option value="ANULADO">Anulado</option>
              </Form.Select>
            </Col>

            <Col md={2}>
              <Button
                variant="primary"
                onClick={consultar}
                disabled={loading}
                className="w-100"
              >
                {loading ? "Consultando..." : "Consultar"}
              </Button>
            </Col>

            <Col md={12}>
              <Form.Label>Buscar</Form.Label>
              <Form.Control
                value={filtros.buscar}
                onChange={(e) => setF("buscar", e.target.value)}
                placeholder="Buscar por número, cliente/proveedor, concepto..."
              />
            </Col>
          </Row>

          {err ? (
            <Alert variant="danger" className="mt-3">
              {err}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 mt-3">
        <Card.Body>
          <div className="table-responsive">
            <Table bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: 90, textAlign: "center" }}>Edita</th>
                  <th style={{ width: 90, textAlign: "center" }}>Anula</th>
                  <th style={{ width: 100 }}>Fecha</th>
                  <th style={{ width: 130 }}>Número</th>
                  <th style={{ width: 100 }}>Tipo</th>
                  <th>Cliente / Proveedor</th>
                  <th style={{ width: 120 }}>Movimiento</th>
                  <th>Concepto</th>
                  <th style={{ width: 120, textAlign: "right" }}>Imp. A</th>
                  <th style={{ width: 120, textAlign: "right" }}>Imp. B</th>
                  <th style={{ width: 120, textAlign: "right" }}>Flete</th>
                  <th style={{ width: 120, textAlign: "right" }}>Saldo</th>
                  <th style={{ width: 110, textAlign: "center" }}>Estado</th>
                </tr>
              </thead>

              <tbody>
                {ajustesFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="text-center text-muted">
                      No hay ajustes para mostrar.
                    </td>
                  </tr>
                ) : (
                  ajustesFiltrados.map((a) => {
                    const bloqueado =
                      a.estado === "ANULADO" || Number(a.aplicadoTotal || 0) > 0;

                    return (
                      <tr key={a._id}>
                        <td style={{ textAlign: "center" }}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={bloqueado}
                            onClick={() => abrirEditar(a)}
                          >
                            Editar
                          </Button>
                        </td>

                        <td style={{ textAlign: "center" }}>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={bloqueado}
                            onClick={() => anular(a)}
                          >
                            Anular
                          </Button>
                        </td>

                        <td>{fmtFecha(a.fecha)}</td>
                        <td>{a.numero}</td>
                        <td>{a.entidadTipo}</td>
                        <td>{nombreEntidad(a)}</td>
                        <td>{a.movimiento === "A_COBRAR" ? "A cobrar" : "A pagar"}</td>
                        <td>{a.concepto}</td>
                        <td style={{ textAlign: "right" }}>$ {fmtMoney(a.importeA)}</td>
                        <td style={{ textAlign: "right" }}>$ {fmtMoney(a.importeB)}</td>
                        <td style={{ textAlign: "right" }}>$ {fmtMoney(a.importeFlete)}</td>
                        <td style={{ textAlign: "right", fontWeight: 700 }}>
                          $ {fmtMoney(a.saldoTotal)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            style={{
                              display: "inline-block",
                              minWidth: 80,
                              padding: "2px 8px",
                              borderRadius: 10,
                              color: "white",
                              fontWeight: 700,
                              background: estadoColor(a.estado),
                            }}
                          >
                            {a.estado}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <ModalAjusteCtaCte
        show={showModal}
        ajuste={ajusteEdit}
        onHide={(saved) => {
          setShowModal(false);
          setAjusteEdit(null);

          if (saved) {
            setTimeout(() => consultar(), 150);
          }
        }}
      />
    </Container>
  );
}